import { useEffect, useMemo, useState } from 'react';
import { GridApi, Column, ColumnApi, IRowNode } from 'ag-grid-community';
import cx from 'classnames';

type Props = {
  displayName: string;
  api: GridApi;
  columnApi: ColumnApi;
  column: Column;
  onSelectionChanged?: (payload: { api: GridApi }) => void;
};

enum CheckboxStatus {
  EMPTY,
  CHECKED,
  INDETERMINATE,
}

/* eslint-disable sonarjs/cognitive-complexity */
const HeaderRenderer: React.FC<Props> = ({
  api: tableApi,
  column,
  columnApi,
  onSelectionChanged = () => null,
  displayName,
}) => {
  const colDef = column.getColDef();
  const currentSortDir = column.getSort();
  const nextOrder = !currentSortDir || currentSortDir === 'desc' ? 'asc' : 'desc';
  const [selectedNodes, setSelectedNodes] = useState(() => tableApi.getSelectedNodes());

  const onSortRequested = (): void => {
    const columnsState = columnApi.getColumnState().map((col) => {
      col.sort = col.colId !== column.getColId() ? null : nextOrder;
      return col;
    });
    columnApi.applyColumnState({ state: columnsState });
    tableApi.refreshHeader();
  };

  const sort =
    colDef.sortable && currentSortDir ? (
      <span
        onClick={onSortRequested}
        className="ag-header-icon ag-header-label-icon ag-sort-ascending-icon"
        aria-hidden="true"
      >
        <span className={`ag-icon ag-icon-${nextOrder}`} unselectable="on" role="presentation" />
      </span>
    ) : null;

  const checkboxStatus = useMemo(() => {
    const currentPage = tableApi.paginationGetCurrentPage();
    const pageSize = tableApi.paginationGetPageSize();
    const pageStart = currentPage * pageSize;
    const pageEnd = pageStart + pageSize;

    const nodesOnCurrentPage: IRowNode[] = [];
    tableApi.forEachNode((node) => {
      if (node.rowIndex !== null && node.rowIndex >= pageStart && node.rowIndex < pageEnd) {
        nodesOnCurrentPage.push(node);
      }
    });

    const selectableNodesOnPage = nodesOnCurrentPage.filter((node) => node.selectable);
    const selectedNodesOnPage = selectedNodes.filter(
      (node) => node.rowIndex !== null && node.rowIndex >= pageStart && node.rowIndex < pageEnd,
    );

    if (!selectedNodesOnPage.length) {
      return CheckboxStatus.EMPTY;
    }

    if (selectableNodesOnPage.length > 0 && selectedNodesOnPage.length >= selectableNodesOnPage.length) {
      return CheckboxStatus.CHECKED;
    }

    return CheckboxStatus.INDETERMINATE;
  }, [tableApi, selectedNodes]);

  const selectAllRows = (checked: boolean): void => {
    tableApi.forEachNode((row) => {
      if (row.id) {
        tableApi.getRowNode(row.id)?.setSelected(checked);
      }
    });
  };

  const onChange = (): void => {
    selectAllRows(checkboxStatus !== CheckboxStatus.CHECKED);

    onSelectionChanged?.({ api: tableApi });
  };

  const getCheckboxStatus = (): string =>
    ({
      [CheckboxStatus.INDETERMINATE]: 'ag-indeterminate',
      [CheckboxStatus.CHECKED]: 'ag-checked',
      [CheckboxStatus.EMPTY]: '',
    })[checkboxStatus];

  useEffect(() => {
    const handleSelectionChanged = (): void => {
      setSelectedNodes(tableApi.getSelectedNodes());
    };

    tableApi.addEventListener('selectionChanged', handleSelectionChanged);

    return () => {
      tableApi.removeEventListener('selectionChanged', handleSelectionChanged);
    };
  }, [tableApi]);

  return (
    <div>
      {colDef?.headerCheckboxSelection && (
        <div data-test-id="creatives-header-checkbox" role="presentation" onClick={onChange} className="relative">
          <div
            data-test-id="creatives-header-checkbox-status"
            className={cx(`ag-input-wrapper ag-checkbox-input-wrapper -top-[5px] ${getCheckboxStatus()}`)}
          />
        </div>
      )}
      {colDef.sortable ? (
        <div role="presentation" className="flex space-x-0" onClick={onSortRequested}>
          {displayName} {sort}
        </div>
      ) : (
        <div className="sub-header-sm absolute bottom-1 ml-1.5">{displayName}</div>
      )}
    </div>
  );
};
/* eslint-enable sonarjs/cognitive-complexity */

export default HeaderRenderer;
